import React, { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import {
  BasePaginateProps,
  NavigationTooltips,
} from '../../types/BasePaginateProps';
import { Nullable } from '../../types/Nullable';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { NumberInput } from '../NumberInput/NumberInput';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import {
  bounded,
  MIN_PAGE_INDEX,
  renderPageDisplay,
  testHasValidProps,
} from './utils';

interface Props extends BasePaginateProps {
  /** Paginator name */
  name: string;
  /** Disables going to a specific page index but still supports back/next navigation */
  disableGoTo?: boolean;
  /** Customize tooltips */
  getTooltips?: ({
    pageIndex,
    totalCount,
  }: {
    pageIndex: number;
    totalCount: number;
  }) => NavigationTooltips;
  /** If Paginate is loading, the tooltip to display */
  loadingTooltip?: string;
  /** Configures supported paginate modes */
  mode?: 'count';
}

const PAGE_INDEX_START = 1; // Paginate uses 1-based indexing

export const Paginate = memo(
  ({
    disabled,
    disableGoTo,
    disabledControls,
    isLoading,
    getTooltips,
    loadingTooltip,
    mode,
    name,
    pageIndex,
    pageSize = 10,
    totalCount,
    onUpdatePageIndex,
  }: Props) => {
    const intl = useIntl();
    const formatNumber = useFormatNumber();

    if (pageIndex < MIN_PAGE_INDEX) {
      throw new Error(`Page index is ${MIN_PAGE_INDEX}-based`);
    }

    const pageCount = Math.ceil(totalCount / pageSize);

    const isCountMode = mode === 'count';
    const lastPageIndex = isCountMode ? totalCount : pageCount;

    const disableFirst =
      disabled || disabledControls?.first || pageIndex <= MIN_PAGE_INDEX;
    const disablePrevious =
      disabled || disabledControls?.previous || pageIndex <= MIN_PAGE_INDEX;
    const disableNext =
      disabled || disabledControls?.next || pageIndex >= lastPageIndex;
    const disableLast =
      disabled || disabledControls?.last || pageIndex >= lastPageIndex;

    const tooltips = getTooltips?.({ pageIndex, totalCount }) ?? {};

    const handleFirstPage = () => onUpdatePageIndex(PAGE_INDEX_START);

    const handleLastPage = () => onUpdatePageIndex(lastPageIndex);

    const handlePreviousPage = () => {
      const updatedPageIndex = pageIndex - 1;
      onUpdatePageIndex(
        isCountMode && updatedPageIndex === 0 ? totalCount : updatedPageIndex,
      );
    };

    const handleNextPage = () => {
      const updatedPageIndex = pageIndex + 1;
      onUpdatePageIndex(
        isCountMode && updatedPageIndex > lastPageIndex
          ? PAGE_INDEX_START
          : updatedPageIndex,
      );
    };

    const handleSetPage = (updatedPageIndex: Nullable<number>) => {
      onUpdatePageIndex(updatedPageIndex ?? PAGE_INDEX_START);
    };

    const hasValidProps = testHasValidProps({
      pageIndex,
      pageSize,
      totalCount,
    });
    const canPaginate = isCountMode || (hasValidProps && totalCount > pageSize);

    const pageOptions = useMemo(
      () =>
        Array.from(
          { length: Number.isInteger(pageCount) ? pageCount : 0 },
          (_, i) => {
            const pageIndex: number = i + 1;
            return {
              value: pageIndex,
              label: renderPageDisplay({
                pageIndex,
                pageSize,
                totalCount,
                formatNumber,
              }),
            };
          },
        ),
      [pageCount],
    );

    let summary;
    if (disableGoTo) {
      summary = (
        <Text flex="none" variant="body-medium">
          <FormattedMessage
            id="Paginate.ofLabel1"
            defaultMessage="{pageIndex} of {count}"
            description='Pagination summary "of" label. Example: pageIndex = "1", count = "10"'
            values={{
              pageIndex: formatNumber(pageIndex),
              count: formatNumber(bounded(totalCount)),
            }}
          />
        </Text>
      );
    } else {
      summary = isCountMode ? (
        <Layout align="center" spacing={2}>
          <NumberInput
            disabled={disabled}
            max={lastPageIndex}
            min={PAGE_INDEX_START}
            mode="digits"
            name={`set ${name} page`}
            value={pageIndex}
            onChange={handleSetPage}
          />
          <Text flex="none">
            <FormattedMessage
              id="Paginate.ofLabel2"
              defaultMessage="of {count}"
              description='Pagination summary "of" label. Example: count = "10"'
              values={{
                count: formatNumber(bounded(totalCount)),
              }}
            />
          </Text>
        </Layout>
      ) : (
        <ActionsMenu
          enablePortal
          disabled={disabled}
          actions={pageOptions}
          icon="chevron-down"
          name={name}
          size="s"
          text={intl.formatMessage(
            {
              id: 'Paginate.ofLabel3',
              defaultMessage: '{pageDisplay} of {count}',
              description:
                'Pagination summary "of" label with page display. Example: pageDisplay = "1-10", count = "100"',
            },
            {
              pageDisplay: renderPageDisplay({
                pageIndex,
                pageSize,
                totalCount,
                formatNumber,
              }),
              count: formatNumber(bounded(totalCount)),
            },
          )}
          tooltip={intl.formatMessage({
            id: 'Paginate.goToPageTooltip',
            defaultMessage: 'Go to page',
            description: 'Tooltip for go to page actions menu',
          })}
          onActionClick={onUpdatePageIndex}
        />
      );
    }

    return (
      <Layout align="center" flex="none">
        {summary}
        {canPaginate && (
          <Layout preset="icons">
            <IconButton
              disabled={disableFirst}
              icon="double-arrow-left"
              size="s"
              tooltip={
                tooltips.first ??
                intl.formatMessage({
                  id: 'Paginate.firstTooltip',
                  defaultMessage: 'First',
                  description: 'Tooltip for first page button',
                })
              }
              onClick={handleFirstPage}
            />
            <IconButton
              disabled={disablePrevious}
              icon="chevron-left"
              size="s"
              tooltip={
                tooltips.previous ??
                intl.formatMessage({
                  id: 'Paginate.previousTooltip',
                  defaultMessage: 'Previous',
                  description: 'Tooltip for previous page button',
                })
              }
              onClick={handlePreviousPage}
            />
            <IconButton
              disabled={disableNext}
              icon="chevron-right"
              size="s"
              tooltip={
                tooltips.next ??
                intl.formatMessage({
                  id: 'Paginate.nextTooltip',
                  defaultMessage: 'Next',
                  description: 'Tooltip for next page button',
                })
              }
              onClick={handleNextPage}
            />
            <IconButton
              disabled={disableLast}
              icon="double-arrow-right"
              size="s"
              tooltip={
                tooltips.last ??
                intl.formatMessage({
                  id: 'Paginate.lastTooltip',
                  defaultMessage: 'Last',
                  description: 'Tooltip for last page button',
                })
              }
              onClick={handleLastPage}
            />
            {isLoading && (
              <Tooltip tooltip={loadingTooltip}>
                <LoadingSpinner />
              </Tooltip>
            )}
          </Layout>
        )}
      </Layout>
    );
  },
);
